import { useMemo } from 'react';

import qs from 'qs';
import { useParams } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import useHashmapConverter from 'hooks/useHashmapConverter';
import { useLogicCheckerFilterHook } from 'hooks/useLogicCheckerFilterHook';
import { useQueryState } from 'hooks/useQueryState';

import { useQueryGetLogicCheckerCategoryResults } from 'api/queries/getLogicCheckerCategoryResults.query';
import { useQueryGetValidationTypeDrillDown } from 'api/queries/getValidationTypeDrillDown.query';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';

import { getCheck } from 'utilitys/logicQualityCheck.utils';

interface Props {
    displayLevel3Activities: boolean;
}

export const useLogicCheckerBreadCrumbs = ({ displayLevel3Activities = false }: Props) => {
    const { id: versionId, category, check } = useParams<{ id: string; category?: string; check?: string }>();
    const { idList } = useHashmapConverter(versionId);
    const fileMetaDataId = idList.length > 0 ? parseInt(idList.join(',')) : undefined;
    const filters = useLogicCheckerFilterHook();
    const { data: logicCheckerData } = useQueryGetLogicCheckerCategoryResults({
        fileMetaDataId,
        filter: {
            filters,
        },
    });
    const parsedCategory = category ? category.replace('_', '/') : '';
    const parsedCheck = check ? check.replace('_', '/') : '';
    const [accessor] = useQueryState('accessor');
    const [rowId] = useQueryState('rowId');

    const pickedCheck = getCheck({
        categoriesSummaryRows: logicCheckerData?.categoriesSummaryRows,
        parsedCategory,
        parsedCheck,
    });

    const { data: validationTypeDrillDown } = useQueryGetValidationTypeDrillDown({
        fileMetaDataId,
        componentId: pickedCheck?.validationId,
        filter: {
            filters,
        },
    });

    const cell = useMemo(() => {
        if (rowId && accessor && validationTypeDrillDown?.rows) {
            const row = validationTypeDrillDown.rows[rowId as string];
            return row[`drillDownParams-${accessor}`];
        } else {
            return undefined;
        }
    }, [rowId, accessor, validationTypeDrillDown]);
    const rowAccessor = cell?.rowAccessor || null;
    const baseLocation = AppRoutes.buildPath(AppRoutes.DASHBOARD_PLANNING_PATH, riskConfig.logicQualityCheck.link);

    const activitiesDrilldown = rowAccessor ? `Activities Drilldown (${rowAccessor})` : `Activities Drilldown`;

    const queryString = qs.stringify({ accessor, rowId }, { addQueryPrefix: true, skipNulls: true });
    const activitiesDrilldownLink = `/activities${queryString}`;

    const breadcrumbs = [
        {
            link: `${baseLocation}/${versionId}`,
            title: 'Summary',
        },
    ];
    category &&
        breadcrumbs.push({
            link: `${baseLocation}/${versionId}/${category}`,
            title: category,
        });

    check &&
        !displayLevel3Activities &&
        breadcrumbs.push({
            link: `${baseLocation}/${versionId}/${category}/${check}`,
            title: check,
        });

    check &&
        displayLevel3Activities &&
        breadcrumbs.push(
            {
                link: `${baseLocation}/${versionId}/${category}/${check}`,
                title: check,
            },
            {
                link: `${baseLocation}/${versionId}/${category}/${check}${activitiesDrilldownLink}`,
                title: activitiesDrilldown,
            },
        );
    return { breadcrumbs };
};
